import React, { useEffect, useState, useCallback } from 'react';
import { merchantApi } from '../../api/config';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChartLine, faFileAlt, faMoneyBillWave, faCalendarDay, faBuilding,
  faPercent, faCoins, faHandHoldingUsd, faChartPie, faUsers, faGift, faFilter, faTags,
  faUserShield, faArrowDown, faArrowUp, faClock, faTriangleExclamation
} from '@fortawesome/free-solid-svg-icons';
import DataTable, { Column } from '../../components/shared/DataTable';
import { UiPageHeader, UiStatsGrid, UiFilterPanel, UiSelect } from '../../components/ui';
import type { StatCardDef } from '../../components/ui';
import { fmtMoney } from '../../utils/formatMoney';
import { getLabelColor } from '../../utils/labelColors';
import P2pReportPanel, { P2pReportSummary } from '../../components/P2pReportPanel';

// แสดงวันที่/เวลาแบบไทย ใช้บนหัวรายงาน (กันดูผิดช่วงวัน)
const fmtDateTH = (d?: string | null) => {
  if (!d) return '-';
  const dt = new Date(d.length <= 10 ? `${d}T00:00:00` : d);
  if (isNaN(dt.getTime())) return '-';
  return dt.toLocaleDateString('th-TH', { day: '2-digit', month: 'short', year: 'numeric' });
};
const fmtDateTimeTH = (d?: string | null) => {
  if (!d) return '';
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return '';
  return dt.toLocaleString('th-TH', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
};

// amber = category tint ของฝั่ง Sub-Reseller (สี semantic ไม่ใช่สีแบรนด์ tenant)
const AMBER_GRADIENT = 'linear-gradient(135deg, #f59e0b, #d97706)';

// Tab segmented control — pattern เดียวกับ ResellerSubResellersPage
const TAB_BTN_BASE = 'inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all';
const TAB_BTN_ACTIVE = 'bg-white dark:bg-slate-600 text-gray-800 dark:text-slate-100 shadow-sm';
const TAB_BTN_INACTIVE = 'text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-slate-200';

interface SubSummaryRow {
  sub_reseller: { id: string; name: string; email: string; fee_rate: number; payout_fee_rate: number; payin_spread: number; payout_spread: number };
  payin_amount: number; payin_count: number;
  payout_amount: number; payout_count: number;
  total_amount: number;
}
interface SubSummary {
  total: { payin_amount: number; payout_amount: number; payin_count: number; payout_count: number; total_amount: number };
  by_sub: SubSummaryRow[];
}

const ResellerReportsPage: React.FC = () => {
  const today = new Date(new Date().getTime() + 7 * 60 * 60 * 1000).toISOString().split('T')[0];
  const resellerUser = JSON.parse(localStorage.getItem('reseller_user') || '{}');
  const isSubReseller = !!resellerUser?.parent_reseller_id;

  // draft (แก้ในฟอร์ม) vs applied (dateRange — ตัวที่ใช้ยิง API) apply เมื่อกดค้นหาเท่านั้น
  const [dateFrom, setDateFrom] = useState(today);
  const [dateTo, setDateTo] = useState(today);
  const [dateRange, setDateRange] = useState({ start_date: today, end_date: today });
  const emptyReports = {
    merchants: [],
    summary: { total_orders: 0, total_revenue: 0, success_rate: 0 },
    period: null as null | { start?: string; end?: string; generated_at?: string }
  };
  const [reports, setReports] = useState<any>(emptyReports);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [subSummary, setSubSummary] = useState<SubSummary>({
    total: { payin_amount: 0, payout_amount: 0, payin_count: 0, payout_count: 0, total_amount: 0 },
    by_sub: []
  });
  const [loading, setLoading] = useState(true);
  const [p2p, setP2p] = useState<P2pReportSummary | null>(null);
  const [tab, setTab] = useState<'merchants' | 'sub'>('merchants');
  const [companyFilter, setCompanyFilter] = useState('');
  const [labelFilter, setLabelFilter] = useState('');

  const loadReports = useCallback(async () => {
    try {
      setLoading(true);
      setLoadError(null);
      const qs = `start_date=${dateRange.start_date}&end_date=${dateRange.end_date}`;

      // /reseller/reports = ตัวหลัก ถ้าล้มเหลวต้องเคลียร์ของเก่า (กันโชว์เลขค้างคนละช่วงวัน)
      // sub-commissions = ตัวเสริม ให้ล้มเหลวเงียบ ๆ ไม่ล้มทั้งหน้า
      const [reportRes, subRes] = await Promise.all([
        merchantApi.get(`/reseller/reports?${qs}`),
        isSubReseller
          ? Promise.resolve(null)
          : merchantApi.get(`/reseller/sub-commissions/summary?${qs}`).catch(() => null)
      ]);
      setReports(reportRes.data.data);
      if (subRes) setSubSummary(subRes.data.data);
      // 💧 P2P (แยก) ของร้านในสังกัด ตามช่วงวันที่
      const pFrom = new Date(`${dateRange.start_date}T00:00:00`).toISOString();
      const pTo = new Date(`${dateRange.end_date}T23:59:59`).toISOString();
      const p2pRes = await merchantApi.get('/reseller/p2p-report', { params: { date_from: pFrom, date_to: pTo } }).catch(() => null);
      setP2p(p2pRes?.data?.success ? (p2pRes.data.data.summary ?? null) : null);
    } catch (error: any) {
      // เคลียร์ข้อมูลเก่าทั้งหมด ไม่ให้ตัวเลขช่วงก่อนหน้าค้างบนจอจนเข้าใจผิด
      setReports({
        merchants: [],
        summary: { total_orders: 0, total_revenue: 0, success_rate: 0 },
        period: null
      });
      setSubSummary({ total: { payin_amount: 0, payout_amount: 0, payin_count: 0, payout_count: 0, total_amount: 0 }, by_sub: [] });
      setP2p(null);
      setLoadError(error.response?.data?.message || error.message || 'โหลดรายงานไม่สำเร็จ');
      toast.error('ไม่สามารถโหลดรายงานได้: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  }, [dateRange, isSubReseller]);

  useEffect(() => {
    loadReports();
  }, [loadReports]);

  const handleSearch = () => {
    setDateRange({ start_date: dateFrom, end_date: dateTo });
  };

  const handleReset = () => {
    setDateFrom(today);
    setDateTo(today);
    setCompanyFilter('');
    setLabelFilter('');
    setDateRange({ start_date: today, end_date: today });
  };

  const allMerchants = reports.merchants || [];
  const getCompany = (m: any) => m.company_name || m.merchant_name || '';
  const companyNames = Array.from(new Set(allMerchants.map((m: any) => getCompany(m)).filter(Boolean))).sort() as string[];
  const allLabels = Array.from(new Set(
    allMerchants.flatMap((m: any) => m.reseller_labels || [])
  )).sort() as string[];
  const filteredMerchants = allMerchants.filter((m: any) => {
    if (companyFilter && getCompany(m) !== companyFilter) return false;
    if (labelFilter && !(m.reseller_labels || []).includes(labelFilter)) return false;
    return true;
  });

  // การ์ดสรุปต้อง match กับ filter ที่เลือก (ทั้ง company และ label) ไม่งั้นการ์ดกับตารางไม่ตรงกัน
  const isFiltered = !!companyFilter || !!labelFilter;
  const filteredSummary = isFiltered
    ? {
        total_orders: filteredMerchants.reduce((s: number, m: any) => s + Number(m.total_orders || 0), 0),
        base_revenue: filteredMerchants.reduce((s: number, m: any) => s + Number(m.base_revenue || 0), 0),
        total_revenue: filteredMerchants.reduce((s: number, m: any) => s + Number(m.total_revenue || 0), 0),
        total_fee: filteredMerchants.reduce((s: number, m: any) => s + Number(m.total_fee || 0), 0),
        net_revenue: filteredMerchants.reduce((s: number, m: any) => s + Number(m.net_revenue || 0), 0),
        success_rate: (() => {
          const totalOrders = filteredMerchants.reduce((s: number, m: any) => s + Number(m.total_orders || 0), 0);
          const paidOrders = filteredMerchants.reduce((s: number, m: any) => s + Number(m.paid_orders || 0), 0);
          return totalOrders > 0 ? (paidOrders / totalOrders) * 100 : 0;
        })(),
        total_payouts: filteredMerchants.reduce((s: number, m: any) => s + Number(m.total_payouts || 0), 0),
        total_payout_amount: filteredMerchants.reduce((s: number, m: any) => s + Number(m.total_payout_amount || 0), 0),
        total_payout_fee: filteredMerchants.reduce((s: number, m: any) => s + Number(m.total_payout_fee || 0), 0),
        payin_commission_count: filteredMerchants.reduce((s: number, m: any) => s + Number(m.payin_commission_count || 0), 0),
        payin_commission_amount: filteredMerchants.reduce((s: number, m: any) => s + Number(m.payin_commission_amount || 0), 0),
        payout_commission_count: filteredMerchants.reduce((s: number, m: any) => s + Number(m.payout_commission_count || 0), 0),
        payout_commission_amount: filteredMerchants.reduce((s: number, m: any) => s + Number(m.payout_commission_amount || 0), 0),
        total_commission_amount: filteredMerchants.reduce((s: number, m: any) => s + Number(m.payin_commission_amount || 0) + Number(m.payout_commission_amount || 0), 0),
      }
    : reports.summary;

  const columns: Column<any>[] = [
    {
      key: 'merchant',
      header: 'Merchant',
      render: (m) => (
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
               style={{ background: 'var(--theme-primary-light)', color: 'var(--theme-primary)' }}>
            <FontAwesomeIcon icon={faBuilding} className="text-xs" />
          </div>
          <div>
            <p className="font-medium text-gray-800 dark:text-slate-100 text-sm">{m.merchant_name}</p>
            <p className="text-xs text-gray-400 dark:text-slate-500">{m.merchant_email}</p>
            {(m.reseller_labels || []).length > 0 && (
              <div className="flex flex-wrap gap-1 mt-1">
                {(m.reseller_labels as string[]).map((label: string) => (
                  <span key={label} className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium ${getLabelColor(label)}`}>
                    {label}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      )
    },
    {
      key: 'commission_rate',
      header: 'Rate',
      render: (m) => (
        <div className="text-xs">
          <div className="text-gray-600 dark:text-slate-300">P.In {m.fee_percentage}% <span className="text-green-600 dark:text-green-400 font-medium">(+{m.commission_rate}%)</span></div>
          <div className="text-gray-400 dark:text-slate-500">P.Out {m.payout_fee_percentage || 0}% <span className="text-indigo-500 dark:text-indigo-400 font-medium">(+{m.payout_commission_rate || 0}%)</span></div>
        </div>
      )
    },
    {
      key: 'total_orders',
      header: 'Orders',
      sortable: true,
      render: (m) => <span className="font-bold text-gray-800 dark:text-slate-100">{m.total_orders}</span>
    },
    {
      key: 'payin',
      header: 'Payin',
      sortable: true,
      render: (m) => (
        <div>
          {(m.paid_orders || 0) > 0 ? (
            <>
              <span className="font-semibold text-blue-600 dark:text-blue-400">{m.paid_orders} รายการ</span>
              <div className="text-[11px] text-gray-400 dark:text-slate-500 tabular-nums">฿{fmtMoney(m.base_revenue || 0)}</div>
              <div className="text-[11px] text-amber-600 dark:text-amber-400 tabular-nums">Fee: ฿{fmtMoney(m.total_fee || 0)}</div>
            </>
          ) : (
            <span className="text-gray-300 dark:text-slate-600">-</span>
          )}
        </div>
      )
    },
    {
      key: 'payin_commission_amount',
      header: 'Com. Payin',
      sortable: true,
      render: (m) => (
        <div>
          {(m.payin_commission_count || 0) > 0 ? (
            <>
              <span className="font-semibold text-green-600 dark:text-green-400 tabular-nums">+฿{fmtMoney(m.payin_commission_amount || 0)}</span>
              <div className="text-[11px] text-gray-400 dark:text-slate-500">{m.payin_commission_count} รายการ</div>
            </>
          ) : (
            <span className="text-gray-300 dark:text-slate-600">-</span>
          )}
        </div>
      )
    },
    {
      key: 'payout',
      header: 'Payout',
      sortable: true,
      render: (m) => (
        <div>
          {(m.total_payouts || 0) > 0 ? (
            <>
              <span className="font-semibold text-indigo-600 dark:text-indigo-400">{m.total_payouts} รายการ</span>
              <div className="text-[11px] text-gray-400 dark:text-slate-500 tabular-nums">฿{fmtMoney(m.total_payout_amount || 0)}</div>
              <div className="text-[11px] text-amber-600 dark:text-amber-400 tabular-nums">Fee: ฿{fmtMoney(m.total_payout_fee || 0)}</div>
            </>
          ) : (
            <span className="text-gray-300 dark:text-slate-600">-</span>
          )}
        </div>
      )
    },
    {
      key: 'payout_commission_amount',
      header: 'Com. Payout',
      sortable: true,
      render: (m) => (
        <div>
          {(m.payout_commission_count || 0) > 0 ? (
            <>
              <span className="font-semibold text-indigo-600 dark:text-indigo-400 tabular-nums">+฿{fmtMoney(m.payout_commission_amount || 0)}</span>
              <div className="text-[11px] text-gray-400 dark:text-slate-500">{m.payout_commission_count} รายการ</div>
            </>
          ) : (
            <span className="text-gray-300 dark:text-slate-600">-</span>
          )}
        </div>
      )
    },
    {
      key: 'net_revenue',
      header: 'ยอดสุทธิ',
      sortable: true,
      render: (m) => (
        <span className="font-bold text-green-600 dark:text-green-400 text-base tabular-nums">
          ฿{fmtMoney(m.net_revenue || 0)}
        </span>
      )
    },
    {
      key: 'success_rate',
      header: 'Success %',
      sortable: true,
      render: (m) => (
        <div className="flex items-center gap-2">
          <div className="flex-1 h-2 bg-gray-100 dark:bg-slate-700 rounded-full overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-500"
              style={{
                width: `${m.success_rate}%`,
                background: m.success_rate >= 80 ? 'linear-gradient(to right, #10b981, #34d399)' :
                  m.success_rate >= 50 ? 'linear-gradient(to right, #f59e0b, #fbbf24)' :
                    'linear-gradient(to right, #ef4444, #f87171)'
              }}
            ></div>
          </div>
          <span className={`text-xs font-bold min-w-[40px] text-right
            ${m.success_rate >= 80 ? 'text-green-600 dark:text-green-400' : m.success_rate >= 50 ? 'text-amber-600 dark:text-amber-400' : 'text-red-500 dark:text-red-400'}`}>
            {parseFloat(m.success_rate).toFixed(1)}%
          </span>
        </div>
      )
    }
  ];

  // รวม commission จาก merchant + sub-reseller spread
  const merchantCommTotal = filteredSummary.total_commission_amount || 0;
  const subSpreadTotal = !isSubReseller ? subSummary.total.total_amount : 0;
  const grandCommTotal = merchantCommTotal + subSpreadTotal;

  const payoutStatCards: StatCardDef[] = [
    {
      icon: faHandHoldingUsd,
      iconClassName: 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400',
      label: 'Payout ทั้งหมด',
      value: <span className="text-indigo-600 dark:text-indigo-400">{(filteredSummary.total_payouts || 0).toLocaleString()} รายการ</span>,
      subValue: <span className="text-xs text-gray-400 dark:text-slate-500 tabular-nums">ยอดถอนรวม ฿{fmtMoney(filteredSummary.total_payout_amount || 0)}</span>
    },
    {
      icon: faPercent,
      iconClassName: 'bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400',
      label: 'ค่าธรรมเนียมถอน',
      value: <span className="text-amber-600 dark:text-amber-400 tabular-nums">฿{fmtMoney(filteredSummary.total_payout_fee || 0)}</span>
    },
    {
      icon: faCoins,
      iconClassName: 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400',
      label: 'Fee รวม (Payin + Payout)',
      value: (
        <span className="text-purple-600 dark:text-purple-400 tabular-nums">
          ฿{fmtMoney((filteredSummary.total_fee || 0) + (filteredSummary.total_payout_fee || 0))}
        </span>
      )
    }
  ];

  return (
    <div className="max-w-full">
      {/* Header */}
      <UiPageHeader
        icon={faChartPie}
        title="Reports"
        below={
          <p className="text-white/85 text-sm mt-1 flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className="inline-flex items-center gap-1.5 font-semibold">
              <FontAwesomeIcon icon={faCalendarDay} className="text-white/70 text-xs" />
              {fmtDateTH(dateRange.start_date)} – {fmtDateTH(dateRange.end_date)}
            </span>
            {reports.period?.generated_at && (
              <span className="text-white/60 text-xs inline-flex items-center gap-1">
                <FontAwesomeIcon icon={faClock} className="text-[10px]" />
                ข้อมูล ณ {fmtDateTimeTH(reports.period.generated_at)}
              </span>
            )}
          </p>
        }
      />

      {/* Filters — วันที่ = draft/applied (กดค้นหาถึงยิง API); Company/Label กรอง client-side ทันที */}
      <UiFilterPanel
        dateFrom={dateFrom}
        dateTo={dateTo}
        onDateFromChange={setDateFrom}
        onDateToChange={setDateTo}
        onSearch={handleSearch}
        onReset={handleReset}
        loading={loading}
      >
        {/* Company/Label filters — เฉพาะ tab merchants */}
        {tab === 'merchants' && (
          <>
            <UiSelect
              label="Company"
              icon={faFilter}
              value={companyFilter}
              onChange={(e) => setCompanyFilter(e.target.value)}
              wrapperClassName="w-full sm:w-48"
            >
              <option value="">ทุก Company</option>
              {companyNames.map((n) => <option key={n} value={n}>{n}</option>)}
            </UiSelect>
            {allLabels.length > 0 && (
              <UiSelect
                label="Label"
                icon={faTags}
                value={labelFilter}
                onChange={(e) => setLabelFilter(e.target.value)}
                wrapperClassName="w-full sm:w-44"
              >
                <option value="">ทุก Label</option>
                {allLabels.map((l) => <option key={l} value={l}>{l}</option>)}
              </UiSelect>
            )}
          </>
        )}
      </UiFilterPanel>

      {loadError && (
        <div className="mb-6 rounded-xl border border-red-200 dark:border-red-500/30 bg-red-50 dark:bg-red-500/10 px-4 py-3 flex items-start gap-3">
          <FontAwesomeIcon icon={faTriangleExclamation} className="text-red-500 dark:text-red-400 mt-0.5" />
          <div className="text-sm">
            <p className="font-semibold text-red-700 dark:text-red-400">โหลดข้อมูลช่วงวันที่นี้ไม่สำเร็จ</p>
            <p className="text-red-600/80 dark:text-red-400/80">ตัวเลขถูกล้างเพื่อไม่ให้สับสนกับช่วงก่อนหน้า — กรุณาเลือกช่วงวันใหม่หรือลองใหม่อีกครั้ง ({loadError})</p>
          </div>
        </div>
      )}

      <P2pReportPanel summary={p2p} />

      {/* Tab Switch — แสดงเฉพาะ top-level reseller */}
      {!isSubReseller && (
        <div className="mb-6">
          <div className="inline-flex items-center bg-gray-100 dark:bg-slate-700 p-1 rounded-xl">
            <button onClick={() => setTab('merchants')}
              className={`${TAB_BTN_BASE} ${tab === 'merchants' ? TAB_BTN_ACTIVE : TAB_BTN_INACTIVE}`}>
              <FontAwesomeIcon icon={faUsers} className="text-xs" />
              Merchant Reports
            </button>
            <button onClick={() => setTab('sub')}
              className={`${TAB_BTN_BASE} ${tab === 'sub' ? TAB_BTN_ACTIVE : TAB_BTN_INACTIVE}`}>
              <FontAwesomeIcon icon={faUserShield} className="text-xs" />
              Sub-Reseller Spread
              {subSpreadTotal > 0 && (
                <span className="px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 dark:bg-amber-500/15 text-amber-700 dark:text-amber-400">
                  +฿{fmtMoney(subSpreadTotal)}
                </span>
              )}
            </button>
          </div>
        </div>
      )}

      {/* ══════════ TAB: MERCHANT REPORTS ══════════ */}
      {tab === 'merchants' && (
        <>
          {/* ช่วงข้อมูล + สถานะ filter — บอกให้ชัดว่าตัวเลขทั้งหมดคือช่วงไหน/กรองอะไรอยู่ (กันงง) */}
          <div className="mb-4 flex flex-wrap items-center gap-2 text-sm">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg font-semibold"
              style={{ background: 'var(--theme-primary-light)', color: 'var(--theme-primary)' }}>
              <FontAwesomeIcon icon={faCalendarDay} />
              ช่วงข้อมูล {fmtDateTH(dateRange.start_date)} – {fmtDateTH(dateRange.end_date)}
            </span>
            {reports.period?.generated_at && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gray-50 dark:bg-slate-700/50 border border-gray-200 dark:border-slate-600 text-gray-500 dark:text-slate-400 text-xs">
                <FontAwesomeIcon icon={faClock} className="text-gray-400 dark:text-slate-500" />
                อัปเดตล่าสุด {fmtDateTimeTH(reports.period.generated_at)}
              </span>
            )}
            {isFiltered && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/30 text-amber-700 dark:text-amber-400 text-xs font-medium">
                <FontAwesomeIcon icon={faFilter} className="text-amber-500 dark:text-amber-400" />
                กรองอยู่: {companyFilter || 'ทุก Company'}{labelFilter ? ` · ${labelFilter}` : ''} ({filteredMerchants.length}/{allMerchants.length} ร้าน)
              </span>
            )}
          </div>

          {/* Summary Cards — custom grid (การ์ด Commission มี breakdown ซ้อน + col-span จึงไม่ใช้ UiStatsGrid) */}
          <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 gap-4 mb-6">
            <div className="bg-white dark:bg-slate-800 rounded-xl p-4 shadow-theme">
              <div className="w-9 h-9 rounded-lg bg-gradient-primary flex items-center justify-center text-white mb-2">
                <FontAwesomeIcon icon={faFileAlt} className="text-sm" />
              </div>
              <p className="text-xs text-gray-500 dark:text-slate-400 uppercase font-medium">Total Orders</p>
              <p className="text-xl font-bold text-primary">{(filteredSummary.total_orders || 0).toLocaleString()}</p>
            </div>

            <div className="bg-white dark:bg-slate-800 rounded-xl p-4 shadow-theme">
              <div className="w-9 h-9 rounded-lg bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 flex items-center justify-center mb-2">
                <FontAwesomeIcon icon={faMoneyBillWave} className="text-sm" />
              </div>
              <p className="text-xs text-gray-500 dark:text-slate-400 uppercase font-medium">ยอดฝาก (Base)</p>
              <p className="text-lg font-bold tabular-nums" style={{ color: 'var(--theme-accent)' }}>฿{fmtMoney(filteredSummary.base_revenue || 0)}</p>
            </div>

            <div className="bg-white dark:bg-slate-800 rounded-xl p-4 shadow-theme border-l-4 border-red-400 dark:border-red-500">
              <div className="w-9 h-9 rounded-lg bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 flex items-center justify-center mb-2">
                <FontAwesomeIcon icon={faPercent} className="text-sm" />
              </div>
              <p className="text-xs text-gray-500 dark:text-slate-400 uppercase font-medium">ค่าธรรมเนียม</p>
              <p className="text-lg font-bold text-red-600 dark:text-red-400 tabular-nums">-฿{fmtMoney(filteredSummary.total_fee || 0)}</p>
            </div>

            <div className="bg-white dark:bg-slate-800 rounded-xl p-4 shadow-theme border-l-4 border-green-400 dark:border-green-500">
              <div className="w-9 h-9 rounded-lg bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 flex items-center justify-center mb-2">
                <FontAwesomeIcon icon={faHandHoldingUsd} className="text-sm" />
              </div>
              <p className="text-xs text-gray-500 dark:text-slate-400 uppercase font-medium">ยอดสุทธิ</p>
              <p className="text-lg font-bold text-green-600 dark:text-green-400 tabular-nums">฿{fmtMoney(filteredSummary.net_revenue || 0)}</p>
            </div>

            {/* Commission รวม — รวม merchant + sub-reseller spread */}
            <div className="bg-white dark:bg-slate-800 rounded-xl p-4 shadow-theme border-l-4 col-span-2 sm:col-span-1"
              style={{ borderLeftColor: 'var(--theme-primary)' }}>
              <div className="flex items-center justify-between mb-2">
                <div className="w-9 h-9 rounded-lg bg-gradient-primary flex items-center justify-center text-white">
                  <FontAwesomeIcon icon={faGift} className="text-sm" />
                </div>
                {!isSubReseller && subSpreadTotal > 0 && (
                  <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-amber-100 dark:bg-amber-500/15 text-amber-600 dark:text-amber-400">+Sub</span>
                )}
              </div>
              <p className="text-xs text-gray-500 dark:text-slate-400 uppercase font-medium">Commission รวม</p>
              <p className="text-lg font-bold text-primary tabular-nums">+฿{fmtMoney(grandCommTotal)}</p>
              {((filteredSummary.payin_commission_amount || 0) > 0 || (filteredSummary.payout_commission_amount || 0) > 0) && (
                <div className="mt-1.5 space-y-0.5">
                  <div className="flex items-center justify-between text-[11px]">
                    <span className="text-gray-400 dark:text-slate-500 flex items-center gap-1">
                      <FontAwesomeIcon icon={faArrowDown} className="text-[9px]" /> Com. Payin
                    </span>
                    <span className="text-green-600 dark:text-green-400 font-medium tabular-nums">+฿{fmtMoney(filteredSummary.payin_commission_amount || 0)}</span>
                  </div>
                  <div className="flex items-center justify-between text-[11px]">
                    <span className="text-gray-400 dark:text-slate-500 flex items-center gap-1">
                      <FontAwesomeIcon icon={faArrowUp} className="text-[9px]" /> Com. Payout
                    </span>
                    <span className="text-indigo-600 dark:text-indigo-400 font-medium tabular-nums">+฿{fmtMoney(filteredSummary.payout_commission_amount || 0)}</span>
                  </div>
                </div>
              )}
              {!isSubReseller && subSpreadTotal > 0 && (
                <div className="mt-1.5 space-y-0.5">
                  <div className="flex items-center justify-between text-[11px]">
                    <span className="text-gray-400 dark:text-slate-500 flex items-center gap-1">
                      <FontAwesomeIcon icon={faUsers} className="text-[9px]" /> Merchant
                    </span>
                    <span className="text-primary font-medium tabular-nums">+฿{fmtMoney(merchantCommTotal)}</span>
                  </div>
                  <div className="flex items-center justify-between text-[11px]">
                    <span className="text-gray-400 dark:text-slate-500 flex items-center gap-1">
                      <FontAwesomeIcon icon={faUserShield} className="text-[9px]" /> Sub-Reseller
                    </span>
                    <span className="text-amber-500 dark:text-amber-400 font-medium tabular-nums">+฿{fmtMoney(subSpreadTotal)}</span>
                  </div>
                </div>
              )}
            </div>

            <div className="bg-white dark:bg-slate-800 rounded-xl p-4 shadow-theme border-l-4 border-green-400 dark:border-green-500">
              <div className="w-9 h-9 rounded-lg bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 flex items-center justify-center mb-2">
                <FontAwesomeIcon icon={faChartLine} className="text-sm" />
              </div>
              <p className="text-xs text-gray-500 dark:text-slate-400 uppercase font-medium">Success Rate</p>
              <p className="text-lg font-bold text-green-600 dark:text-green-400">{(filteredSummary.success_rate || 0).toFixed(1)}%</p>
            </div>
          </div>

          {/* Commission Detail */}
          {merchantCommTotal > 0 && (
            <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-theme p-6 mb-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-lg bg-gradient-primary flex items-center justify-center text-white">
                  <FontAwesomeIcon icon={faGift} />
                </div>
                <div>
                  <h3 className="text-sm font-bold text-gray-800 dark:text-slate-100">Commission จาก Merchants</h3>
                  <p className="text-xs text-gray-500 dark:text-slate-400">รายได้ค่าคอมมิชชั่นจาก Merchant ที่อยู่ในสายของคุณ</p>
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div className="bg-gray-50 dark:bg-slate-700/50 rounded-xl p-4 border-l-4" style={{ borderLeftColor: 'var(--theme-primary)' }}>
                  <p className="text-xs text-gray-500 dark:text-slate-400 uppercase font-medium">Commission รวม</p>
                  <p className="text-2xl font-bold text-primary tabular-nums">+฿{fmtMoney(merchantCommTotal)}</p>
                  <p className="text-xs text-gray-400 dark:text-slate-500 mt-1">
                    {((filteredSummary.payin_commission_count || 0) + (filteredSummary.payout_commission_count || 0)).toLocaleString()} รายการ
                  </p>
                </div>
                <div className="bg-gray-50 dark:bg-slate-700/50 rounded-xl p-4 border-l-4 border-green-400 dark:border-green-500">
                  <p className="text-xs text-gray-500 dark:text-slate-400 uppercase font-medium">Payin Commission</p>
                  <p className="text-xl font-bold text-green-600 dark:text-green-400 tabular-nums">+฿{fmtMoney(filteredSummary.payin_commission_amount || 0)}</p>
                  <p className="text-xs text-gray-400 dark:text-slate-500 mt-1">{(filteredSummary.payin_commission_count || 0).toLocaleString()} รายการ</p>
                </div>
                <div className="bg-gray-50 dark:bg-slate-700/50 rounded-xl p-4 border-l-4 border-indigo-400 dark:border-indigo-500">
                  <p className="text-xs text-gray-500 dark:text-slate-400 uppercase font-medium">Payout Commission</p>
                  <p className="text-xl font-bold text-indigo-600 dark:text-indigo-400 tabular-nums">+฿{fmtMoney(filteredSummary.payout_commission_amount || 0)}</p>
                  <p className="text-xs text-gray-400 dark:text-slate-500 mt-1">{(filteredSummary.payout_commission_count || 0).toLocaleString()} รายการ</p>
                </div>
                <div className="bg-gray-50 dark:bg-slate-700/50 rounded-xl p-4 border-l-4 border-gray-300 dark:border-slate-500">
                  <p className="text-xs text-gray-500 dark:text-slate-400 uppercase font-medium">เฉลี่ย/รายการ</p>
                  <p className="text-xl font-bold text-gray-700 dark:text-slate-200 tabular-nums">
                    ฿{fmtMoney(((filteredSummary.payin_commission_count || 0) + (filteredSummary.payout_commission_count || 0)) > 0
                      ? (merchantCommTotal / ((filteredSummary.payin_commission_count || 0) + (filteredSummary.payout_commission_count || 0)))
                      : 0)}
                  </p>
                  <p className="text-xs text-gray-400 dark:text-slate-500 mt-1">ต่อรายการ</p>
                </div>
              </div>
            </div>
          )}

          {/* Payout Summary */}
          {(filteredSummary.total_payouts > 0) && (
            <UiStatsGrid cols={3} cards={payoutStatCards} />
          )}

          {/* Merchants DataTable */}
          <DataTable
            data={filteredMerchants}
            columns={columns}
            loading={loading}
            emptyMessage="ไม่มีข้อมูลในช่วงเวลานี้"
            emptyIcon={<FontAwesomeIcon icon={faChartPie} className="text-5xl text-gray-200 dark:text-slate-600" />}
            pagination={{ pageSize: 10, showPageSizeSelector: true }}
            headerContent={
              <div className="flex items-center gap-3">
                <FontAwesomeIcon icon={faUsers} className="text-lg" style={{ color: 'var(--theme-primary)' }} />
                <h2 className="text-lg font-bold text-gray-800 dark:text-slate-100">รายงานตาม Merchant</h2>
              </div>
            }
            hoverable
          />
        </>
      )}

      {/* ══════════ TAB: SUB-RESELLER SPREAD ══════════ */}
      {tab === 'sub' && !isSubReseller && (() => {
        const subColumns: Column<SubSummaryRow>[] = [
          {
            key: 'sub_reseller',
            header: 'Sub-Reseller',
            render: (row) => {
              const initials = row.sub_reseller.name.split(' ').map((w: string) => w[0]).join('').toUpperCase().slice(0, 2);
              return (
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-xl flex items-center justify-center text-sm font-bold text-white flex-shrink-0 shadow-sm"
                       style={{ background: AMBER_GRADIENT }}>
                    {initials}
                  </div>
                  <div>
                    <p className="font-semibold text-gray-800 dark:text-slate-100 text-sm leading-tight">{row.sub_reseller.name}</p>
                    <p className="text-xs text-gray-400 dark:text-slate-500 leading-tight">{row.sub_reseller.email}</p>
                  </div>
                </div>
              );
            }
          },
          {
            key: 'rate',
            header: 'Rate (P.In / P.Out)',
            render: (row) => (
              <div className="inline-flex flex-col items-center gap-1">
                <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-green-100 dark:bg-green-500/15 text-green-700 dark:text-green-400">
                  {row.sub_reseller.fee_rate}%
                </span>
                <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-indigo-50 dark:bg-indigo-500/15 text-indigo-600 dark:text-indigo-400">
                  {row.sub_reseller.payout_fee_rate}%
                </span>
              </div>
            )
          },
          {
            key: 'spread',
            header: 'Spread ที่ได้',
            render: (row) => (
              <div className="inline-flex flex-col items-center gap-1">
                <span className={`px-2 py-0.5 rounded-md text-[11px] font-bold
                  ${row.sub_reseller.payin_spread > 0 ? 'bg-green-50 dark:bg-green-500/15 text-green-600 dark:text-green-400' : 'bg-gray-100 dark:bg-slate-700 text-gray-400 dark:text-slate-500'}`}>
                  +{row.sub_reseller.payin_spread}%
                </span>
                <span className={`px-2 py-0.5 rounded-md text-[11px] font-bold
                  ${row.sub_reseller.payout_spread > 0 ? 'bg-indigo-50 dark:bg-indigo-500/15 text-indigo-600 dark:text-indigo-400' : 'bg-gray-100 dark:bg-slate-700 text-gray-400 dark:text-slate-500'}`}>
                  +{row.sub_reseller.payout_spread}%
                </span>
              </div>
            )
          },
          {
            key: 'payin_amount',
            header: 'Payin Spread',
            sortable: true,
            render: (row) => (
              row.payin_count > 0 ? (
                <div>
                  <p className="text-sm font-bold text-green-600 dark:text-green-400 tabular-nums">+฿{fmtMoney(row.payin_amount)}</p>
                  <p className="text-xs text-gray-400 dark:text-slate-500">{row.payin_count} รายการ</p>
                </div>
              ) : <span className="text-gray-300 dark:text-slate-600">—</span>
            )
          },
          {
            key: 'payout_amount',
            header: 'Payout Spread',
            sortable: true,
            render: (row) => (
              row.payout_count > 0 ? (
                <div>
                  <p className="text-sm font-bold text-indigo-600 dark:text-indigo-400 tabular-nums">+฿{fmtMoney(row.payout_amount)}</p>
                  <p className="text-xs text-gray-400 dark:text-slate-500">{row.payout_count} รายการ</p>
                </div>
              ) : <span className="text-gray-300 dark:text-slate-600">—</span>
            )
          },
          {
            key: 'total_amount',
            header: 'รวม',
            sortable: true,
            render: (row) => (
              <span className="text-base font-bold text-amber-600 dark:text-amber-400 tabular-nums">+฿{fmtMoney(row.total_amount)}</span>
            )
          }
        ];

        const totalRow = (
          <tr>
            <td colSpan={3} className="px-4 py-3 text-sm font-bold text-gray-700 dark:text-slate-200">รวมทั้งหมด</td>
            <td className="px-4 py-3 text-left font-bold text-green-600 dark:text-green-400 tabular-nums">
              +฿{fmtMoney(subSummary.total.payin_amount)}
            </td>
            <td className="px-4 py-3 text-left font-bold text-indigo-600 dark:text-indigo-400 tabular-nums">
              +฿{fmtMoney(subSummary.total.payout_amount)}
            </td>
            <td className="px-4 py-3 text-left text-lg font-bold text-amber-600 dark:text-amber-400 tabular-nums">
              +฿{fmtMoney(subSummary.total.total_amount)}
            </td>
          </tr>
        );

        const subStatCards: StatCardDef[] = [
          {
            icon: faUserShield,
            iconClassName: 'text-white shadow-md',
            iconStyle: { background: AMBER_GRADIENT },
            label: 'Parent Spread รวม',
            value: <span className="text-amber-600 dark:text-amber-400 tabular-nums">+฿{fmtMoney(subSummary.total.total_amount)}</span>,
            subValue: (
              <span className="text-xs text-gray-400 dark:text-slate-500">
                {subSummary.total.payin_count + subSummary.total.payout_count} รายการ
                <span className="mx-1.5 text-gray-300 dark:text-slate-600">·</span>
                {subSummary.by_sub.length} Sub
              </span>
            )
          },
          {
            icon: faArrowDown,
            iconClassName: 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400',
            label: 'Payin Spread',
            value: <span className="text-green-600 dark:text-green-400 tabular-nums">+฿{fmtMoney(subSummary.total.payin_amount)}</span>,
            subValue: <span className="text-xs text-gray-400 dark:text-slate-500">{subSummary.total.payin_count} รายการ</span>
          },
          {
            icon: faArrowUp,
            iconClassName: 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400',
            label: 'Payout Spread',
            value: <span className="text-indigo-600 dark:text-indigo-400 tabular-nums">+฿{fmtMoney(subSummary.total.payout_amount)}</span>,
            subValue: <span className="text-xs text-gray-400 dark:text-slate-500">{subSummary.total.payout_count} รายการ</span>
          }
        ];

        return (
          <>
            {/* Summary Cards */}
            <UiStatsGrid cols={3} cards={subStatCards} />

            {/* Breakdown DataTable */}
            <DataTable
              data={subSummary.by_sub}
              columns={subColumns}
              loading={loading}
              emptyMessage="ไม่มี Sub-Reseller Spread ในช่วงวันที่นี้"
              emptyIcon={<FontAwesomeIcon icon={faUserShield} className="text-5xl text-gray-200 dark:text-slate-600" />}
              keyExtractor={(row) => row.sub_reseller.id}
              hoverable
              pagination={false}
              footer={subSummary.by_sub.length > 0 ? totalRow : undefined}
              headerContent={
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center text-white"
                       style={{ background: AMBER_GRADIENT }}>
                    <FontAwesomeIcon icon={faCoins} className="text-sm" />
                  </div>
                  <div>
                    <h2 className="text-sm font-bold text-gray-800 dark:text-slate-100">แยกตาม Sub-Reseller</h2>
                    <p className="text-xs text-gray-400 dark:text-slate-500">{subSummary.by_sub.length} รายการ</p>
                  </div>
                </div>
              }
            />
          </>
        );
      })()}
    </div>
  );
};

export default ResellerReportsPage;
